/**
 * Booking-tool → host notifications.
 *
 * Three kinds of host consume the "user is done, close me" signal:
 *
 * 1. React hosts (`shopify-storefront`, `custom-made`, `rp-booking-tool`) pass
 *    an `onClose` callback — handled by the caller, not here.
 * 2. Non-React hosts listened for the Stencil-era `crm-bt-close` DOM event on
 *    `window` (see `apps/headless-commerce/.../ClientBookingToolWrapper.tsx`).
 * 3. The CRM modal embeds the tool in an **iframe**.
 *
 * A DOM event cannot cross a frame boundary, so (2) alone is invisible to (3):
 * `window.dispatchEvent` only ever reaches the iframe's own window. For the
 * modal to close we must also post the event to the parent frame.
 */

/** Legacy Stencil event name — kept so existing host listeners keep working. */
export const BOOKING_TOOL_CLOSE_EVENT = 'crm-bt-close'

/** Shape posted to the parent frame. Mirrors the `CustomEvent` detail. */
export interface BookingToolCloseMessage {
  type: typeof BOOKING_TOOL_CLOSE_EVENT
  bookingId?: string
}

/** True when the tool is rendered inside an iframe rather than as the top document. */
const isFramed = () => window.parent !== window

/**
 * Announce that the user finished and the tool should be dismissed.
 *
 * Fires the `crm-bt-close` DOM event (same-document hosts) and, when framed,
 * the equivalent postMessage (iframe hosts). Safe to call in both contexts —
 * the postMessage is skipped when there is no parent frame.
 *
 * `targetOrigin` is `'*'` because the embedding CRM origin is not known to this
 * package, and the payload carries no secret: the host supplied `bookingId` in
 * the iframe URL in the first place. Matches
 * `apps/rp-active-customers-list/.../EmbeddedAuthBootstrap.tsx`.
 */
export const emitBookingToolClose = (bookingId?: string) => {
  if (typeof window === 'undefined') return

  window.dispatchEvent(
    new CustomEvent(BOOKING_TOOL_CLOSE_EVENT, { detail: { bookingId } }),
  )

  if (!isFramed()) return

  const message: BookingToolCloseMessage = {
    type: BOOKING_TOOL_CLOSE_EVENT,
    bookingId,
  }

  try {
    window.parent.postMessage(message, '*')
  } catch {
    // A detached or restricted parent must never block the close flow.
  }
}
